import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import JSZip from "jszip";
import { api } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import { CREATE } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { Upload, X, Download, Loader2, Sparkles, Check, Shuffle, Save, Trash2, SlidersHorizontal } from "lucide-react";

const CATEGORIES = [
  { v: "fashion", label: "Fashion" },
  { v: "minuman", label: "Minuman" },
  { v: "makanan", label: "Makanan" },
  { v: "kosmetik", label: "Kosmetik" },
  { v: "elektronik", label: "Elektronik" },
  { v: "aksesoris", label: "Aksesoris" },
  { v: "sepatu", label: "Sepatu" },
  { v: "furniture", label: "Furniture" },
];

const OPTIONS = {
  gender: [
    { v: "female", label: "Female" },
    { v: "male", label: "Male" },
  ],
  age: [
    { v: "young_adult", label: "Young Adult" },
    { v: "adult", label: "Adult" },
    { v: "mature", label: "Mature" },
  ],
  pose: [
    { v: "front", label: "Full Body — Front" },
    { v: "three_quarter", label: "Full Body — 3/4" },
    { v: "dynamic", label: "Full Body — Side / Walking" },
    { v: "premium", label: "Full Body — Editorial" },
    { v: "back", label: "Full Body — Back View" },
    { v: "sitting", label: "Seated — Full Body" },
    { v: "medium", label: "Waist Up — Medium Shot" },
    { v: "close_up", label: "Close-up Detail" },
  ],
  pose_product: [
    { v: "on_table", label: "On Table - Center" },
    { v: "on_table_angle", label: "On Table 45°" },
    { v: "top_view", label: "Top View Flat Lay" },
    { v: "bar_counter", label: "Bar Counter" },
    { v: "shelf", label: "On Shelf" },
    { v: "hand_held", label: "Hand Held" },
    { v: "front_view", label: "Front View" },
    { v: "side_view", label: "Side View" },
    { v: "close_up", label: "Close-up Detail" },
    { v: "lifestyle", label: "Lifestyle Context" },
    { v: "stacked", label: "Stacked / Group" },
    { v: "hanging", label: "Hanging" },
    { v: "in_box", label: "In Box / Packaging" },
    { v: "with_props", label: "With Props" },
  ],
  background: [
    { v: "studio", label: "Studio" },
    { v: "outdoor", label: "Outdoor" },
    { v: "indoor", label: "Indoor" },
    { v: "luxury_fashion", label: "Luxury Fashion" },
    { v: "minimalist", label: "Minimalist" },
    { v: "wooden_table", label: "Wooden Table" },
    { v: "marble_table", label: "Marble Table" },
    { v: "bar_counter", label: "Bar Counter" },
    { v: "kitchen", label: "Kitchen" },
    { v: "shelf", label: "Shelf" },
  ],
};

const AR = [
  { v: "1:1", testid: CREATE.ar_1_1, ratio: "1 / 1" },
  { v: "4:5", testid: CREATE.ar_4_5, ratio: "4 / 5" },
  { v: "9:16", testid: CREATE.ar_9_16, ratio: "9 / 16" },
  { v: "16:9", testid: CREATE.ar_16_9, ratio: "16 / 9" },
];

const FULL_BODY_POSES = new Set([
  "front",
  "three_quarter",
  "dynamic",
  "premium",
  "back",
  "sitting",
]);

const LOAD_STEPS_SINGLE = [
  "Analyzing product details",
  "Preserving product structure",
  "Configuring pose & lighting",
  "Rendering final image",
];

export const SERIES_SHOTS = [
  { key: "front", label: "Full-body front", locked: true },
  { key: "three_quarter", label: "Full-body 3/4" },
  { key: "dynamic", label: "Side / dynamic" },
  { key: "medium", label: "Medium shot" },
  { key: "detail", label: "Close-up detail" },
  { key: "premium", label: "Premium editorial" },
];
const PRODUCT_SERIES_SHOTS = [
  { key: "front", label: "Product front", locked: true },
  { key: "three_quarter", label: "Product 3/4" },
  { key: "dynamic", label: "Dynamic product" },
  { key: "medium", label: "Medium product" },
  { key: "detail", label: "Product detail" },
  { key: "premium", label: "Premium campaign" },
];
export const SERIES_SIZE = SERIES_SHOTS.length;

const AGE_GROUPS = [
  { v: "adult", label: "Dewasa" },
  { v: "child", label: "Anak" },
];

const ETHNICITIES = [
  { v: "all", label: "All" },
  { v: "european", label: "European" },
  { v: "east_asian", label: "East Asian" },
  { v: "south_asian", label: "South Asian" },
  { v: "black", label: "Black" },
  { v: "latinx", label: "Latinx" },
  { v: "middle_eastern", label: "Middle Eastern" },
];

const POLL_MS = 3000;

const slug = (s) => (s || "photo").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function Create() {
  const { user, refresh } = useAuth();
  const [mode, setMode] = useState("series");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [category, setCategory] = useState("fashion");
  const [withModel, setWithModel] = useState(true);
  const [settings, setSettings] = useState({
    gender: "female",
    age: "young_adult",
    pose: "front",
    background: "studio",
    aspect_ratio: "4:5",
  });
  const [customPrompt, setCustomPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [providerNeedsPortrait, setProviderNeedsPortrait] = useState(false);

  useEffect(() => {
    api
      .get("/config")
      .then((r) => setProviderNeedsPortrait(Boolean(r.data?.full_body_requires_portrait)))
      .catch(() => setProviderNeedsPortrait(false));
  }, []);
  // auto-set withModel & pose when category changes
  useEffect(() => {
    if (category === "fashion") {
      setWithModel(true);
      setSettings((s) => ({ ...s, pose: "front" }));
    } else {
      setWithModel(false);
      setSettings((s) => ({ ...s, pose: "on_table" }));
    }
  }, [category]);

  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [series, setSeries] = useState(null);
  const [selectedShots, setSelectedShots] = useState(() => SERIES_SHOTS.map((s) => s.key));
  const [shotOptions, setShotOptions] = useState({});
  const setShotOption = (key, patch) =>
    setShotOptions((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  const [ethnicity, setEthnicity] = useState("all");
  // Filter usia: model anak hanya masuk untuk konten edukasi, bukan foto produk
  // fashion. Filter etnisitas tidak berlaku di sana karena semua model anak
  // satu etnis dan menampilkan filter itu hanya menambah bingungan.
  const [ageGroup, setAgeGroup] = useState("adult");
  const inputRef = useRef(null);
  const stepTimer = useRef(null);
  const pollTimer = useRef(null);

  // Face refs + saved models
  const [modelTab, setModelTab] = useState("faces"); // 'faces' | 'saved'
  const [faceRefs, setFaceRefs] = useState([]);
  const [savedModels, setSavedModels] = useState([]);
  const [faceRefId, setFaceRefId] = useState("random");
  const [savedModelId, setSavedModelId] = useState(null);
  const [savingModel, setSavingModel] = useState({});
  const effectiveWithModel = withModel || faceRefId !== "random" || Boolean(savedModelId);
  const fullBodyPose = effectiveWithModel && FULL_BODY_POSES.has(settings.pose);
  const activeSeriesShots = effectiveWithModel ? SERIES_SHOTS : PRODUCT_SERIES_SHOTS;
  useEffect(() => {
    if (effectiveWithModel) setWithModel(true);
  }, [effectiveWithModel]);

  const loadFaceRefs = () =>
    api.get("/face-references").then((r) => setFaceRefs(r.data.items || [])).catch(() => {});
  const loadSavedModels = () =>
    api.get("/models").then((r) => setSavedModels(r.data.items || [])).catch(() => {});

  useEffect(() => {
    loadFaceRefs();
    loadSavedModels();
    return () => {
      stepTimer.current && clearInterval(stepTimer.current);
      pollTimer.current && clearTimeout(pollTimer.current);
    };
  }, []);

  // Filter face refs by age group + gender + ethnicity.
  const filteredFaceRefs = useMemo(
    () =>
      faceRefs.filter(
        (r) =>
          r.gender === settings.gender &&
          (r.age_group || "adult") === ageGroup &&
          (ageGroup === "child" || ethnicity === "all" || r.ethnicity === ethnicity),
      ),
    [faceRefs, settings.gender, ethnicity, ageGroup],
  );

  // If the selected face ref no longer matches gender, drop to Random.
  useEffect(() => {
    if (faceRefId !== "random") {
      const ok = filteredFaceRefs.some((r) => r.ref_id === faceRefId);
      if (!ok) setFaceRefId("random");
    }
  }, [filteredFaceRefs, faceRefId]);

  // Model picked from the Dashboard model library (?face_ref=...).
  useEffect(() => {
    const preset = new URLSearchParams(window.location.search).get("face_ref");
    if (!preset || preset === "random") return;
    const match = faceRefs.find((r) => r.ref_id === preset);
    if (!match) return;
    setCategory("fashion");
    setWithModel(true);
    setModelTab("faces");
    setSettings((s) => ({ ...s, gender: match.gender || s.gender }));
    // Preset dari halaman lain bisa menunjuk model anak, jadi ikutkan usia di
    // sini; kalau tidak, filter akan menyembunyikan model yang baru dipilih.
    setAgeGroup(match.age_group || "adult");
    setEthnicity("all");
    setFaceRefId(preset);
    setSavedModelId(null);
  }, [faceRefs]);

  const handleFile = (f) => {
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Only JPG, PNG, or WEBP allowed.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large. Max 10 MB.");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
    setSeries(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const requireKey = () => {
    if (user?.license_activated === false) {
      toast.error("Activate your account with the code from your purchase first.");
      return false;
    }
    if (!user?.has_byok) {
      toast.error("Connect your Gemini API key in Profile first.");
      return false;
    }
    return true;
  };

  const generateSingle = async () => {
    if (!file) return toast.error("Upload a product image first.");
    if (!requireKey()) return;
    setLoading(true);
    setStep(0);
    setResult(null);
    setSeries(null);
    stepTimer.current = setInterval(
      () => setStep((s) => (s + 1) % LOAD_STEPS_SINGLE.length),
      2200,
    );

    const form = new FormData();
    form.append("file", file);
    Object.entries(settings).forEach(([k, v]) => form.append(k, v));
    form.append("category", category);
    form.append("with_model", String(effectiveWithModel));
    if (customPrompt.trim()) form.append("custom_prompt", customPrompt.trim());
    if (effectiveWithModel) {
      if (savedModelId) {
        form.append("saved_model_id", savedModelId);
      } else if (faceRefId && faceRefId !== "random") {
        form.append("face_reference_id", faceRefId);
      }
    }

    try {
      const { data } = await api.post("/generate", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      await refresh();
      toast.success("Generation complete.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Generation failed.");
    } finally {
      clearInterval(stepTimer.current);
      setLoading(false);
    }
  };

  const pollSeries = async (seriesId) => {
    try {
      const { data } = await api.get(`/series/${seriesId}`);
      setSeries(data);
      if (data.running) {
        pollTimer.current = setTimeout(() => pollSeries(seriesId), POLL_MS);
        return;
      }
      setLoading(false);
      await refresh();
      const ok = data.completed;
      const failed = data.total - ok;
      toast.success(
        failed
          ? `${ok} of ${data.total} photos ready. ${failed} can be retried in History.`
          : `All ${ok} product photos rendered.`,
      );
    } catch {
      pollTimer.current = setTimeout(() => pollSeries(seriesId), POLL_MS * 2);
    }
  };

  const generateSeries = async () => {
    if (!file) return toast.error("Upload a product image first.");
    if (!requireKey()) return;
    setLoading(true);
    setResult(null);
    setSeries(null);

    const form = new FormData();
    form.append("file", file);
    ["gender", "age", "background", "aspect_ratio"].forEach((k) =>
      form.append(k, settings[k]),
    );
    form.append("category", category);
    form.append("with_model", String(effectiveWithModel));
    form.append("series_pose", settings.pose);
    form.append("shots", selectedShots.join(","));
    const opts = Object.fromEntries(
      Object.entries(shotOptions).filter(
        ([k, v]) => selectedShots.includes(k) && (v?.note?.trim() || v?.background),
      ),
    );
    if (Object.keys(opts).length) form.append("shot_options", JSON.stringify(opts));
    if (customPrompt.trim()) form.append("custom_prompt", customPrompt.trim());
    if (effectiveWithModel) {
      if (savedModelId) {
        form.append("saved_model_id", savedModelId);
      } else if (faceRefId && faceRefId !== "random") {
        form.append("face_reference_id", faceRefId);
      }
    }

    try {
      const { data } = await api.post("/generate-series", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setSeries(data);
      toast.info(`Photoshoot started — ${data.total} shots rendering.`);
      pollTimer.current = setTimeout(() => pollSeries(data.series_id), POLL_MS);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Series generation failed.");
      setLoading(false);
    }
  };

  const toggleShot = (key) =>
    setSelectedShots((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : activeSeriesShots.map((s) => s.key).filter((k) => k === key || prev.includes(k)),
    );

  const downloadOne = async (path, name) => {
    try {
      const res = await api.get(`/files/${path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed.");
    }
  };

  const downloadAll = async () => {
    if (!series) return;
    const successes = series.jobs.filter(
      (j) => j.status === "completed" && j.generated_path,
    );
    if (successes.length === 0) return;
    toast.info("Packaging your product series…");
    try {
      const zip = new JSZip();
      for (const [i, j] of successes.entries()) {
        const res = await api.get(`/files/${j.generated_path}`, { responseType: "blob" });
        const ext = j.generated_path.split(".").pop();
        zip.file(`${String(i + 1).padStart(2, "0")}-${slug(j.pose_label || j.settings?.pose)}.${ext}`, res.data);
      }
      const blob = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `photo-series-${series.series_id}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Zip download failed.");
    }
  };

  const saveAsModel = async (job) => {
    const name = window.prompt(
      "Name this model (used to reuse the same face on future series):",
      `Model ${(savedModels.length || 0) + 1}`,
    );
    if (!name) return;
    setSavingModel((m) => ({ ...m, [job.job_id]: true }));
    try {
      await api.post("/models", { job_id: job.job_id, name });
      toast.success("Model saved to your library");
      loadSavedModels();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Save failed");
    } finally {
      setSavingModel((m) => ({ ...m, [job.job_id]: false }));
    }
  };

  const deleteSavedModel = async (id) => {
    if (!window.confirm("Delete this saved model?")) return;
    try {
      await api.delete(`/models/${id}`);
      if (savedModelId === id) setSavedModelId(null);
      loadSavedModels();
      toast.success("Model removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const loadingSteps = LOAD_STEPS_SINGLE;
  // BYOK: backend mengirim generation_remaining = null (tanpa batas). Jangan
  // pakai ?? 0 -- null berarti bebas, bukan habis.
  const remaining = user?.generation_remaining;
  const seriesCost = selectedShots.length;
  const seriesInsufficient =
    mode === "series" && remaining != null && remaining < seriesCost;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <div className="mb-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">Studio</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight text-zinc-950">
          Create <span className="italic text-zinc-500">studio image</span>
        </h1>
      </div>

      {/* Mode toggle */}
      <div className="mb-10 inline-flex border border-zinc-900">
        <button
          data-testid={CREATE.modeSeries}
          onClick={() => setMode("series")}
          className={`px-5 py-2.5 text-sm inline-flex items-center gap-2 transition-colors ${
            mode === "series"
              ? "bg-zinc-950 text-[#FBFBF9]"
              : "bg-transparent text-zinc-700 hover:bg-zinc-100"
          }`}
        >
          <Sparkles className="h-4 w-4" /> {SERIES_SIZE}-Photo Series
        </button>
        <button
          data-testid={CREATE.modeSingle}
          onClick={() => setMode("single")}
          className={`px-5 py-2.5 text-sm border-l border-zinc-900 transition-colors ${
            mode === "single"
              ? "bg-zinc-950 text-[#FBFBF9]"
              : "bg-transparent text-zinc-700 hover:bg-zinc-100"
          }`}
        >
          Single Photo
        </button>
      </div>

      {mode === "series" && effectiveWithModel && (
        <div className="mb-8 border-l-2 border-zinc-900 pl-4 py-1">
          <p className="font-serif text-xl text-zinc-950">
            1 product photo <span className="text-zinc-400">→</span> {SERIES_SIZE} professional photos
          </p>
          <p className="mt-1 text-[13px] text-zinc-500">
            {category === "fashion"
              ? "Full-body front · 3/4 · Side/dynamic · Medium · Close-up detail · Premium editorial — same model, same garment, one photoshoot."
              : "Product front · 3/4 · Dynamic · Medium · Close-up detail · Premium campaign — exact product in every image."}
          </p>
        </div>
      )}

      {/* Kategori & Referensi Background */}
      <div className="mb-8 grid md:grid-cols-2 gap-6 p-5 border border-zinc-200 bg-white">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Kategori Produk</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-2 w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2.5 text-sm bg-white">
            {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">Opsi Model</span>
          <div className="mt-2 flex items-center gap-3">
            <button onClick={() => setWithModel(!withModel)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${effectiveWithModel ? "bg-zinc-950" : "bg-zinc-300"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${effectiveWithModel ? "translate-x-6" : "translate-x-1"}`} />
            </button>
            <span className="text-[13px] text-zinc-700">{effectiveWithModel ? "Dengan Model" : "Tanpa Model (di atas meja)"}</span>
          </div>
          <p className="mt-1 text-[11px] text-zinc-500">{effectiveWithModel ? "Pakai wajah referensi & pose model" : "Produk di atas meja / rak — tanpa wajah"}</p>
        </label>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 space-y-8">
          <div>
            <Label>1 · Clothing image</Label>
            <div
              data-testid={CREATE.dropzone}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              className="mt-3 relative border border-dashed border-zinc-300 aspect-[4/5] max-h-[420px] bg-white hover:border-zinc-900 transition-colors cursor-pointer overflow-hidden"
            >
              {preview ? (
                <>
                  <img src={preview} alt="" className="w-full h-full object-contain bg-zinc-50" />
                  <button
                    data-testid={CREATE.removeImage}
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setPreview(null);
                    }}
                    className="absolute top-3 right-3 h-8 w-8 bg-zinc-950 text-white flex items-center justify-center"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
                  <Upload className="h-6 w-6 text-zinc-400" />
                  <p className="font-serif text-lg text-zinc-800">Drop your product</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                    JPG · PNG · WEBP · ≤ 10 MB
                  </p>
                </div>
              )}
              <input
                ref={inputRef}
                data-testid={CREATE.input}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>
          </div>

          <div>
            <Label>2 · Model & scene</Label>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <SelectField
                testid={CREATE.gender}
                label="Gender"
                value={settings.gender}
                onChange={(v) => setSettings((s) => ({ ...s, gender: v }))}
                options={OPTIONS.gender}
              />
              <SelectField
                testid={CREATE.age}
                label="Age"
                value={settings.age}
                onChange={(v) => setSettings((s) => ({ ...s, age: v }))}
                options={OPTIONS.age}
              />
              {mode !== "series" && (
                <SelectField
                  testid={CREATE.pose}
                  label={effectiveWithModel ? "Framing" : "Posisi Produk"}
                  value={settings.pose}
                  onChange={(v) => setSettings((s) => ({ ...s, pose: v }))}
                  options={effectiveWithModel ? OPTIONS.pose : OPTIONS.pose_product}
                />
              )}
              <SelectField
                testid={CREATE.background}
                label="Background"
                value={settings.background}
                onChange={(v) => setSettings((s) => ({ ...s, background: v }))}
                options={OPTIONS.background}
              />
            </div>
          </div>

          <ChooseYourModel
            faceRefs={filteredFaceRefs}
            savedModels={savedModels}
            modelTab={modelTab}
            setModelTab={setModelTab}
            faceRefId={faceRefId}
            setFaceRefId={(id) => {
              setFaceRefId(id);
              setSavedModelId(null);
            }}
            savedModelId={savedModelId}
            setSavedModelId={(id) => {
              setSavedModelId(id);
              if (id) setFaceRefId("random");
            }}
            onDeleteSaved={deleteSavedModel}
            ethnicity={ethnicity}
            setEthnicity={setEthnicity}
            ageGroup={ageGroup}
            setAgeGroup={setAgeGroup}
          />

          {mode === "series" && (
            <ShotPicker
              shots={activeSeriesShots}
              selected={selectedShots}
              onToggle={toggleShot}
              onAll={() => setSelectedShots(activeSeriesShots.map((s) => s.key))}
              options={shotOptions}
              onOption={setShotOption}
              seriesBackground={settings.background}
            />
          )}

          <div>
            <Label>{mode === "series" ? "5 · Aspect ratio" : "4 · Aspect ratio"}</Label>
            <div className="mt-4 grid grid-cols-4 gap-3">
              {AR.map((a) => (
                <button
                  key={a.v}
                  data-testid={a.testid}
                  onClick={() => setSettings((s) => ({ ...s, aspect_ratio: a.v }))}
                  className={`flex flex-col items-center justify-end p-2 pb-3 border transition-colors ${
                    settings.aspect_ratio === a.v
                      ? "border-zinc-900 bg-zinc-950 text-white"
                      : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-500"
                  }`}
                  style={{ aspectRatio: "1 / 1" }}
                >
                  <div
                    className={`border ${
                      settings.aspect_ratio === a.v ? "border-white" : "border-zinc-400"
                    }`}
                    style={{ aspectRatio: a.ratio, width: "60%", maxHeight: "60%" }}
                  />
                  <span className="mt-2 font-mono text-[10px] tracking-wide">{a.v}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>{mode === "series" ? "6 · Custom direction (optional)" : "5 · Custom direction (optional)"}</Label>
            <textarea
              data-testid={CREATE.customPrompt}
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              rows={3}
              placeholder="e.g., golden hour lighting, cinematic mood"
              className="mt-3 w-full border border-zinc-300 focus:border-zinc-900 outline-none px-4 py-3 text-sm bg-white resize-none"
            />
          </div>

          {mode === "series" ? (
            <div>
              {user?.license_activated === false && (
                <div className="mb-4 border border-rose-300 bg-rose-50 p-4">
                  <p className="text-[13px] font-medium text-rose-900">
                    Activate your account to start generating
                  </p>
                  <p className="mt-1 text-[12px] text-rose-800">
                    Paste the activation code from your purchase in Profile. One code
                    activates one account, so your access stays yours.
                  </p>
                  <Link
                    to="/profile"
                    className="mt-3 inline-flex bg-zinc-950 px-4 py-2 text-[13px] text-[#FBFBF9] hover:bg-zinc-800"
                  >
                    Enter activation code
                  </Link>
                </div>
              )}
              {user?.license_activated !== false && !user?.has_byok && (
                <div className="mb-4 border border-amber-500 bg-amber-50 p-4">
                  <p className="text-[13px] font-medium text-amber-900">
                    Generation is locked until you connect your own Gemini API key
                  </p>
                  <p className="mt-1 text-[12px] text-amber-800">
                    You pay Google directly for your images, the app owner does not. Takes
                    about two minutes.
                  </p>
                  <Link
                    to="/profile"
                    className="mt-3 inline-flex bg-zinc-950 px-4 py-2 text-[13px] text-[#FBFBF9] hover:bg-zinc-800"
                  >
                    Connect API key
                  </Link>
                </div>
              )}
              {seriesInsufficient && (
                <p className="mb-3 text-[12px] text-rose-600 border-l-2 border-rose-600 pl-3">
                  You need at least {seriesCost} generations remaining to generate a complete {seriesCost}-photo series. You have {remaining}.
                </p>
              )}
              <button
                data-testid={CREATE.generateSeries}
                onClick={generateSeries}
                disabled={loading || !file || seriesInsufficient}
                className="w-full bg-zinc-950 text-[#FBFBF9] py-4 text-sm tracking-wide hover:bg-zinc-800 disabled:bg-zinc-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Rendering {series ? `${series.completed}/${series.total}` : "series"}…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" /> Generate {seriesCost} Photo{seriesCost === 1 ? "" : "s"} · {seriesCost} credit{seriesCost === 1 ? "" : "s"}
                  </>
                )}
              </button>
            </div>
          ) : (
            <button
              data-testid={CREATE.generate}
              onClick={generateSingle}
              disabled={loading || !file}
              className="w-full bg-zinc-950 text-[#FBFBF9] py-4 text-sm tracking-wide hover:bg-zinc-800 disabled:bg-zinc-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Rendering…
                </>
              ) : (
                "Generate studio image"
              )}
            </button>
          )}
        </div>

        {/* Right: stage */}
        <div className="lg:col-span-7">
          <div className="sticky top-24">
            <Label>Preview</Label>

            {/* Series gallery */}
            {mode === "series" ? (
              <div className="mt-4">
                {loading && !series ? (
                  <div
                    data-testid={CREATE.loader}
                    className="relative aspect-[16/10] bg-zinc-950 flex flex-col items-center justify-center gap-4 overflow-hidden"
                  >
                    <div className="absolute inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent animate-[scan_2.4s_linear_infinite]" />
                    <Loader2 className="h-6 w-6 animate-spin text-[#FBFBF9]" />
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FBFBF9]/80">
                      Uploading product · starting photoshoot…
                    </p>
                  </div>
                ) : series ? (
                  <SeriesGallery
                    series={series}
                    onDownload={downloadOne}
                    onDownloadAll={downloadAll}
                    onSaveModel={saveAsModel}
                    savingModel={savingModel}
                  />
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {activeSeriesShots.filter((s) => selectedShots.includes(s.key)).map((p) => (
                      <div key={p.key} className="aspect-[4/5] bg-zinc-100 border border-zinc-200 flex items-center justify-center p-2 text-center">
                        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400 leading-snug">
                          {p.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-4 grid grid-cols-2 gap-4">
                <StageCard label="Original">
                  {preview ? (
                    <img
                      data-testid={CREATE.original}
                      src={preview}
                      alt=""
                      className="w-full h-full object-contain bg-zinc-50"
                    />
                  ) : (
                    <EmptyStage text="Upload a product" />
                  )}
                </StageCard>
                <StageCard label="Generated">
                  {loading ? (
                    <div
                      data-testid={CREATE.loader}
                      className="absolute inset-0 bg-zinc-950 flex flex-col items-center justify-center gap-4 overflow-hidden"
                    >
                      <div className="absolute inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent animate-[scan_2.4s_linear_infinite]" />
                      <Loader2 className="h-6 w-6 animate-spin text-[#FBFBF9]" />
                      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FBFBF9]/80">
                        {loadingSteps[step]}…
                      </p>
                    </div>
                  ) : result?.generated_path ? (
                    <AuthImage
                      path={result.generated_path}
                      testid={CREATE.generated}
                      alt=""
                      className="w-full h-full object-contain bg-zinc-50"
                    />
                  ) : (
                    <EmptyStage text="Awaiting render" />
                  )}
                </StageCard>
              </div>
            )}

            {mode === "single" && result?.generated_path && !loading && (
              <div className="mt-6 flex items-center justify-between">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                  Job · {result.job_id}
                </p>
                <button
                  data-testid={CREATE.download}
                  onClick={() => downloadOne(result.generated_path, `photo-${result.job_id}.png`)}
                  className="inline-flex items-center gap-2 border border-zinc-900 px-5 py-2.5 text-sm hover:bg-zinc-950 hover:text-[#FBFBF9] transition-colors"
                >
                  <Download className="h-4 w-4" /> Download HD
                </button>
              </div>
            )}
            </div>
            {fullBodyPose && providerNeedsPortrait && ["1:1", "16:9"].includes(settings.aspect_ratio) && (
              <p className="mt-3 text-[12px] text-amber-700 border-l-2 border-amber-600 pl-3">
                Full body di aspect {settings.aspect_ratio} prone terpotong. Recommended 4:5 atau 9:16 untuk
                hasil head-to-toe.
              </p>
            )}
          </div>
      </div>

      <style>{`
        @keyframes scan {
          0% { transform: translateY(-50vh); }
          100% { transform: translateY(50vh); }
        }
      `}</style>
    </div>
  );
}

function SeriesGallery({ series, onDownload, onDownloadAll, onSaveModel, savingModel }) {
  const ok = series.jobs.filter((j) => j.status === "completed");
  const front = ok.find((j) => j.settings?.pose === "front") || ok[0];
  const running = !!series.running;
  const pct = series.jobs.length ? Math.round((ok.length / series.jobs.length) * 100) : 0;
  return (
    <div data-testid={CREATE.seriesGallery}>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <p data-testid="series-progress-label" className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
          Series · {series.series_id} · {ok.length}/{series.jobs.length} ready{running ? " · rendering…" : ""}
        </p>
        <div className="flex items-center gap-2">
          {front && onSaveModel && !running && (
            <button
              data-testid={CREATE.saveModelButton}
              disabled={!!savingModel?.[front.job_id]}
              onClick={() => onSaveModel(front)}
              className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 text-[12px] hover:border-zinc-900 disabled:opacity-50 transition-colors"
            >
              <Save className="h-3.5 w-3.5" /> Save as model
            </button>
          )}
          {ok.length > 0 && !running && (
            <button
              data-testid={CREATE.downloadAll}
              onClick={onDownloadAll}
              className="inline-flex items-center gap-2 border border-zinc-900 px-4 py-2 text-sm hover:bg-zinc-950 hover:text-[#FBFBF9] transition-colors"
            >
              <Download className="h-4 w-4" /> Download all ({ok.length})
            </button>
          )}
        </div>
      </div>
      {running && (
        <div data-testid="series-progress-bar" className="mb-4 h-[2px] bg-zinc-100">
          <div className="h-full bg-zinc-950 transition-[width] duration-700" style={{ width: `${pct}%` }} />
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {series.jobs.map((j) => (
          <div key={j.job_id} data-testid={CREATE.seriesItem} className="group">
            <div className="relative aspect-[4/5] bg-zinc-100 border border-zinc-200 overflow-hidden">
              {j.status === "completed" && j.generated_path ? (
                <AuthImage
                  path={j.generated_path}
                  alt={j.pose_label}
                  className="w-full h-full object-cover"
                />
              ) : j.status === "pending" ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center p-3">
                  {j.started_at ? (
                    <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />
                  ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-zinc-300" />
                  )}
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400">
                    {j.started_at ? "rendering" : "queued"}
                  </span>
                </div>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-center p-3">
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      j.status === "failed" ? "bg-rose-500" : "bg-amber-500"
                    }`}
                  />
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">
                    {j.status}
                  </span>
                  {j.error?.message && (
                    <span className="text-[10px] text-zinc-500 leading-tight">
                      {j.error.message}
                    </span>
                  )}
                </div>
              )}
              <span className="absolute top-2 left-2 bg-[#FBFBF9]/95 font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 text-zinc-800">
                {j.pose_label}
              </span>
            </div>
            {j.status === "completed" && (
              <button
                onClick={() =>
                  onDownload(j.generated_path, `${slug(j.pose_label)}-${j.job_id}.png`)
                }
                className="mt-2 w-full inline-flex items-center justify-center gap-1.5 border border-zinc-300 py-1.5 text-[12px] hover:border-zinc-900 transition-colors"
              >
                <Download className="h-3 w-3" /> Download
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ShotPicker({ shots, selected, onToggle, onAll, options, onOption, seriesBackground }) {
  const [open, setOpen] = useState(null);
  const allSelected = selected.length === shots.length;
  const bgLabel = (v) => OPTIONS.background.find((o) => o.v === v)?.label || v;
  return (
    <div data-testid="shot-picker-section">
      <div className="flex items-center justify-between">
        <Label>4 · Shots · {selected.length} selected · {selected.length} credit{selected.length === 1 ? "" : "s"}</Label>
        {!allSelected && (
          <button
            data-testid="shot-picker-select-all"
            onClick={onAll}
            className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 hover:text-zinc-950 underline-offset-4 hover:underline"
          >
            Select all
          </button>
        )}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-1.5">
        {shots.map((s, i) => {
          const on = selected.includes(s.key);
          const opt = options[s.key] || {};
          const customized = !!(opt.note?.trim() || opt.background);
          const isOpen = open === s.key;
          return (
            <div key={s.key} className={isOpen ? "col-span-2" : ""}>
              <div
                className={`flex items-stretch border transition-colors ${
                  on ? "border-zinc-900 bg-zinc-950 text-[#FBFBF9]" : "border-zinc-200 bg-white text-zinc-500"
                }`}
              >
                <button
                  data-testid={`shot-toggle-${s.key}`}
                  aria-pressed={on}
                  disabled={s.locked}
                  title={s.locked ? "The front shot anchors the model identity and is always included" : undefined}
                  onClick={() => onToggle(s.key)}
                  className={`flex-1 flex items-center gap-2.5 px-3 py-2 text-left text-[12px] ${
                    on ? "" : "hover:text-zinc-800"
                  } ${s.locked ? "cursor-default" : ""}`}
                >
                  <span
                    className={`h-3.5 w-3.5 border flex items-center justify-center shrink-0 ${
                      on ? "border-[#FBFBF9]" : "border-zinc-400"
                    }`}
                  >
                    {on && <Check className="h-2.5 w-2.5" />}
                  </span>
                  <span className="font-mono text-[9px] opacity-60 w-4">{String(i + 1).padStart(2, "0")}</span>
                  <span className="truncate">{s.label}</span>
                  {s.locked && <span className="ml-auto font-mono text-[8px] uppercase tracking-[0.15em] opacity-60">anchor</span>}
                </button>
                {on && (
                  <button
                    data-testid={`shot-direct-${s.key}`}
                    onClick={() => setOpen(isOpen ? null : s.key)}
                    title="Per-shot direction"
                    aria-expanded={isOpen}
                    className={`px-2.5 border-l flex items-center gap-1 font-mono text-[8px] uppercase tracking-[0.15em] transition-colors ${
                      on ? "border-[#FBFBF9]/25 hover:bg-zinc-800" : "border-zinc-200"
                    } ${customized ? "text-[#D4AF37]" : "opacity-70"}`}
                  >
                    <SlidersHorizontal className="h-3 w-3" />
                    {customized ? "set" : ""}
                  </button>
                )}
              </div>
              {isOpen && on && (
                <div data-testid={`shot-direction-panel-${s.key}`} className="border border-t-0 border-zinc-900 bg-white p-3 grid sm:grid-cols-5 gap-3">
                  <label className="sm:col-span-3 block">
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400">Note for this shot</span>
                    <input
                      data-testid={`shot-note-${s.key}`}
                      value={opt.note || ""}
                      maxLength={200}
                      onChange={(e) => onOption(s.key, { note: e.target.value })}
                      placeholder='e.g., hands in pockets, looking off-camera'
                      className="mt-1 w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2 text-[12px] bg-white"
                    />
                  </label>
                  <label className="sm:col-span-2 block">
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400">Backdrop</span>
                    <select
                      data-testid={`shot-background-${s.key}`}
                      value={opt.background || ""}
                      onChange={(e) => onOption(s.key, { background: e.target.value || null })}
                      className="mt-1 w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2 text-[12px] bg-white"
                    >
                      <option value="">Series default ({bgLabel(seriesBackground)})</option>
                      {OPTIONS.background.map((o) => (
                        <option key={o.v} value={o.v}>{o.label}</option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChooseYourModel({
  faceRefs,
  savedModels,
  modelTab,
  setModelTab,
  faceRefId,
  setFaceRefId,
  savedModelId,
  setSavedModelId,
  onDeleteSaved,
  ethnicity,
  setEthnicity,
  ageGroup = "adult",
  setAgeGroup,
}) {
  return (
    <div data-testid={CREATE.chooseModelSection}>
      <div className="flex items-center justify-between">
        <Label>3 · Choose your model</Label>
        <div className="inline-flex border border-zinc-300 text-[11px]">
          <button
            data-testid={CREATE.faceRefTab}
            onClick={() => setModelTab("faces")}
            className={`px-3 py-1.5 font-mono uppercase tracking-[0.2em] transition-colors ${
              modelTab === "faces" ? "bg-zinc-950 text-[#FBFBF9]" : "text-zinc-600"
            }`}
          >
            Faces
          </button>
          <button
            data-testid={CREATE.savedModelsTab}
            onClick={() => setModelTab("saved")}
            className={`px-3 py-1.5 font-mono uppercase tracking-[0.2em] border-l border-zinc-300 transition-colors ${
              modelTab === "saved" ? "bg-zinc-950 text-[#FBFBF9]" : "text-zinc-600"
            }`}
          >
            My Models ({savedModels.length})
          </button>
        </div>
      </div>

      {modelTab === "faces" ? (
        <>
          <div data-testid="age-group-filter" className="mt-3 flex flex-wrap gap-1.5">
            {AGE_GROUPS.map((a) => (
              <button
                key={a.v}
                data-testid={`age-chip-${a.v}`}
                aria-pressed={ageGroup === a.v}
                onClick={() => setAgeGroup(a.v)}
                className={`px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em] border transition-colors ${
                  ageGroup === a.v
                    ? "border-zinc-900 bg-zinc-950 text-[#FBFBF9]"
                    : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-500"
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>
          {ageGroup === "adult" && (
          <div data-testid="ethnicity-filter" className="mt-3 flex flex-wrap gap-1.5">
            {ETHNICITIES.map((e) => (
              <button
                key={e.v}
                data-testid={`ethnicity-chip-${e.v}`}
                aria-pressed={ethnicity === e.v}
                onClick={() => setEthnicity(e.v)}
                className={`px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] border transition-colors ${
                  ethnicity === e.v
                    ? "border-zinc-900 bg-zinc-950 text-[#FBFBF9]"
                    : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-500"
                }`}
              >
                {e.label}
              </button>
            ))}
          </div>
          )}
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-2.5">
          <button
            data-testid={CREATE.faceRefRandom}
            onClick={() => setFaceRefId("random")}
            className={`aspect-[3/4] border flex flex-col items-center justify-center gap-2 p-2 transition-colors ${
              faceRefId === "random"
                ? "border-zinc-900 bg-zinc-950 text-[#FBFBF9]"
                : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-500"
            }`}
          >
            <Shuffle className="h-4 w-4" />
            <span className="font-serif text-sm text-center leading-tight">Random Face</span>
            <span className="font-mono text-[8px] uppercase tracking-[0.2em] opacity-70">AI Picks</span>
          </button>
          {faceRefs.map((r) => (
            <button
              key={r.ref_id}
              data-testid={`${CREATE.faceRefCard}-${r.ref_id}`}
              onClick={() => setFaceRefId(r.ref_id)}
              className={`relative aspect-[3/4] border overflow-hidden text-left transition-colors ${
                faceRefId === r.ref_id
                  ? "border-zinc-900 ring-2 ring-zinc-900"
                  : "border-zinc-200 hover:border-zinc-500"
              }`}
            >
              {r.thumbnail_path ? (
                <AuthImage width={240} path={r.thumbnail_path} className="w-full h-full object-cover" alt={r.name} />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-zinc-100 via-white to-zinc-100 flex flex-col items-center justify-center p-1.5 text-center overflow-hidden">
                  <span className="font-serif text-2xl text-zinc-950 leading-none">
                    {r.name.charAt(0)}
                  </span>
                  <span className="mt-1 font-serif text-[12px] text-zinc-950 leading-tight">
                    {r.name}
                  </span>
                  <span className="mt-1 font-mono text-[7px] uppercase tracking-[0.1em] text-zinc-500 leading-tight line-clamp-2">
                    {r.descriptor}
                  </span>
                </div>
              )}
              {faceRefId === r.ref_id && (
                <span className="absolute top-1.5 right-1.5 h-5 w-5 bg-zinc-950 text-[#FBFBF9] flex items-center justify-center">
                  <Check className="h-3 w-3" />
                </span>
              )}
              {r.thumbnail_path && (
                <span className="absolute bottom-1.5 left-1.5 bg-[#FBFBF9]/90 font-serif text-[11px] px-1.5 py-0.5 text-zinc-800">
                  {r.name}
                </span>
              )}
            </button>
          ))}
          {faceRefs.length === 0 && (
            <p data-testid="face-filter-empty" className="col-span-5 self-center text-[12px] text-zinc-500">
              No {ETHNICITIES.find((e) => e.v === ethnicity)?.label} models for this gender yet.
            </p>
          )}
        </div>
        </>
      ) : (
        <div className="mt-4">
          {savedModels.length === 0 ? (
            <div className="border border-dashed border-zinc-300 py-10 text-center">
              <p className="text-[13px] text-zinc-500">
                No saved models yet. Generate a series, then click <span className="font-medium">Save as model</span> on the result.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2.5">
              <button
                onClick={() => setSavedModelId(null)}
                className={`aspect-[3/4] border flex flex-col items-center justify-center gap-2 p-2 transition-colors ${
                  savedModelId === null
                    ? "border-zinc-900 bg-zinc-950 text-[#FBFBF9]"
                    : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-500"
                }`}
              >
                <span className="font-serif text-sm">None</span>
                <span className="font-mono text-[8px] uppercase tracking-[0.2em] opacity-70">
                  Use faces
                </span>
              </button>
              {savedModels.map((m) => (
                <div
                  key={m.model_id}
                  data-testid={`${CREATE.savedModelCard}-${m.model_id}`}
                  className="relative group"
                >
                  <button
                    onClick={() => setSavedModelId(m.model_id)}
                    className={`relative w-full aspect-[3/4] border overflow-hidden transition-colors ${
                      savedModelId === m.model_id
                        ? "border-zinc-900 ring-2 ring-zinc-900"
                        : "border-zinc-200 hover:border-zinc-500"
                    }`}
                  >
                    <AuthImage width={240} path={m.path} className="w-full h-full object-cover" alt={m.name} />
                    {savedModelId === m.model_id && (
                      <span className="absolute top-1.5 right-1.5 h-5 w-5 bg-zinc-950 text-[#FBFBF9] flex items-center justify-center">
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                    <span className="absolute bottom-1.5 left-1.5 right-1.5 bg-[#FBFBF9]/90 font-serif text-[11px] px-1.5 py-0.5 text-zinc-800 truncate">
                      {m.name}
                    </span>
                  </button>
                  <button
                    onClick={() => onDeleteSaved(m.model_id)}
                    title="Delete"
                    className="absolute top-1.5 left-1.5 h-5 w-5 bg-zinc-950/80 text-[#FBFBF9] hidden group-hover:flex items-center justify-center hover:bg-zinc-950"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Label({ children }) {
  return (
    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{children}</p>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2.5 text-sm bg-white"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function StageCard({ label, children }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-2">{label}</p>
      <div className="relative aspect-[4/5] bg-zinc-100 overflow-hidden border border-zinc-200">
        {children}
      </div>
    </div>
  );
}

function EmptyStage({ text }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">{text}</span>
    </div>
  );
}
